import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, MenuItem, TextField, Typography } from '@mui/material';
import MenuBookOutlined from '@mui/icons-material/MenuBookOutlined';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import EditNoteOutlined from '@mui/icons-material/EditNoteOutlined';
import BusinessOutlined from '@mui/icons-material/BusinessOutlined';
import UpdateOutlined from '@mui/icons-material/UpdateOutlined';
import { DetailPageTemplate } from '../../../components/page-templates/detail-page/DetailPageTemplate';
import { InsightTile } from '../../../components/shared/tiles/InsightTile';
import BackButton from '../../../components/shared/actions/BackButton';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import { UserContext } from '../../../components/shared/useUser';
import { rangeClient, MenuDto, MenuDisplayStatus } from '../../../app/services/api/range';
import useLogError from '../../../hooks/useLogError';
import RangeMenusTab from './components/RangeMenusTab';

const formatDate = (value?: string | null): string => (value ? new Date(value).toLocaleDateString() : '-');

const RangeForm = () => {
  const { t } = useTranslation('range');
  const navigate = useNavigate();
  const { rangeId } = useParams<{ rangeId: string }>();
  const { franchises, selectedFranchise, hasPermissionTo } = useContext(UserContext);
  const { logError, extractErrorMessage } = useLogError();

  const isEditMode = Boolean(rangeId);
  const canWrite = hasPermissionTo(['RangeWrite']);
  const canViewMenus = hasPermissionTo(['MenuRead']);
  const accessibleFranchises = useMemo(
    () => franchises.filter((franchise) => franchise.active !== false && franchise.franchiseId !== undefined),
    [franchises],
  );
  const activeFranchiseId = accessibleFranchises.some(
    (franchise) => franchise.franchiseId === selectedFranchise?.franchiseId,
  ) ? selectedFranchise?.franchiseId : undefined;
  const defaultFranchiseId = activeFranchiseId ?? (
    accessibleFranchises.length === 1 ? accessibleFranchises[0].franchiseId : undefined
  );

  const [isLoading, setIsLoading] = useState(isEditMode);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTabId, setActiveTabId] = useState('overview');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [nameError, setNameError] = useState('');
  const [franchiseId, setFranchiseId] = useState<number | undefined>(defaultFranchiseId);
  const [modifiedDate, setModifiedDate] = useState<string | null>(null);
  const [assignedMenuIds, setAssignedMenuIds] = useState<Set<number>>(new Set());
  const [originalAssignedMenuIds, setOriginalAssignedMenuIds] = useState<Set<number>>(new Set());

  const [menus, setMenus] = useState<MenuDto[]>([]);
  const [isMenusLoading, setIsMenusLoading] = useState(true);

  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!isEditMode) {
      setFranchiseId(defaultFranchiseId);
    }
  }, [defaultFranchiseId, isEditMode]);

  const loadRange = useCallback(async () => {
    if (!rangeId) return;
    setIsLoading(true);
    try {
      const numericId = Number(rangeId);
      const range = await rangeClient.getRange(numericId);
      if (!range) {
        setErrorMessage(t('loadErrorMessage'));
        return;
      }

      const loadedMenus = canViewMenus
        ? await rangeClient.getRangeMenus(numericId)
        : [];

      setName(range.name);
      setDescription(range.description ?? '');
      setFranchiseId(range.franchiseId);
      setModifiedDate(range.modifiedDate ?? null);
      const assignedIds = new Set(loadedMenus.map((menu) => menu.menuId));
      setAssignedMenuIds(assignedIds);
      setOriginalAssignedMenuIds(assignedIds);
    } catch (error) {
      logError(error);
      setErrorMessage(t('loadErrorMessage'));
    } finally {
      setIsLoading(false);
    }
    // logError's identity changes every render (useLogError isn't memoized) - omit it to avoid an infinite fetch loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeId, t, canViewMenus]);

  useEffect(() => {
    loadRange();
  }, [loadRange]);

  useEffect(() => {
    if (!franchiseId || !canViewMenus) {
      setMenus([]);
      setIsMenusLoading(false);
      return;
    }
    let active = true;
    setIsMenusLoading(true);
    rangeClient.getMenus({ page: 1, pageSize: 2147483647 })
      .then((result) => {
        if (active) setMenus(result.data.filter((menu) => menu.franchiseId === franchiseId));
      })
      .catch(() => {
        if (active) setMenus([]);
      })
      .finally(() => {
        if (active) setIsMenusLoading(false);
      });
    return () => {
      active = false;
    };
  }, [franchiseId, canViewMenus]);

  const handleToggleMenu = (menuId: number) => {
    setAssignedMenuIds((prev) => {
      const next = new Set(prev);
      if (next.has(menuId)) next.delete(menuId);
      else next.add(menuId);
      return next;
    });
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setNameError(t('nameRequired'));
      setActiveTabId('overview');
      return;
    }
    if (!franchiseId) {
      setErrorMessage(t('franchiseRequired'));
      return;
    }

    setIsSaving(true);
    try {
      const menuIds = Array.from(assignedMenuIds);

      if (isEditMode && rangeId) {
        await rangeClient.updateRange(Number(rangeId), {
          name: name.trim(),
          description: description.trim() || null,
          menuIds,
        });
        await loadRange();
        setSuccessMessage(t('rangeSavedSuccessMessage'));
      } else {
        await rangeClient.createRange({
          franchiseId,
          name: name.trim(),
          description: description.trim() || null,
          menuIds,
        });
        navigate('/range/ranges', {
          state: { successMessage: t('rangeSavedSuccessMessage') },
        });
      }
    } catch (error) {
      logError(error);
      setErrorMessage(extractErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const assignedMenus = useMemo(
    () => menus.filter((m) => assignedMenuIds.has(m.id)),
    [menus, assignedMenuIds],
  );
  const activeMenusCount = assignedMenus.filter((m) => m.displayStatus === MenuDisplayStatus.Active).length;
  const scheduledMenusCount = assignedMenus.filter((m) => m.displayStatus === MenuDisplayStatus.Scheduled).length;
  const draftMenusCount = assignedMenus.filter((m) => m.displayStatus === MenuDisplayStatus.Draft).length;
  const franchiseForDisplay = accessibleFranchises.find((franchise) => franchise.franchiseId === franchiseId)
    ?? selectedFranchise;

  const handleFranchiseChange = useCallback((value: string) => {
    const nextFranchiseId = value ? Number(value) : undefined;
    if (nextFranchiseId === franchiseId) return;

    setFranchiseId(nextFranchiseId);
    setAssignedMenuIds(new Set());
    setOriginalAssignedMenuIds(new Set());
  }, [franchiseId]);

  const tabs = useMemo(() => {
    const items = [
      {
        id: 'overview',
        label: t('overview'),
        content: (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 480 }}>
            {!isEditMode && accessibleFranchises.length > 1 && (
              <TextField
                select
                label={t('franchise')}
                value={franchiseId ?? ''}
                onChange={(e) => handleFranchiseChange(e.target.value)}
                required
                fullWidth
                disabled={!canWrite || accessibleFranchises.length <= 1}
              >
                <MenuItem value="">
                  <em>{t('selectFranchise')}</em>
                </MenuItem>
                {accessibleFranchises.map((franchise) => (
                  <MenuItem key={franchise.franchiseId}
                    value={franchise.franchiseId}>
                    {franchise.franchiseName}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label={t('name')}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) setNameError('');
              }}
              error={Boolean(nameError)}
              helperText={nameError}
              required
              fullWidth
              disabled={!canWrite}
            />
            <TextField
              label={t('description')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              disabled={!canWrite}
            />
          </Box>
        ),
      },
    ];

    if (canViewMenus) {
      items.push({
        id: 'menus',
        label: t('menus'),
        content: franchiseId ? (
          <RangeMenusTab
            menus={menus}
            isLoading={isMenusLoading}
            assignedMenuIds={assignedMenuIds}
            originalAssignedMenuIds={originalAssignedMenuIds}
            onToggle={handleToggleMenu}
            onMenuClick={(menuId) => navigate(`/range/menus/${menuId}`)}
            readOnly={!canWrite}
          />
        ) : (
          <Typography color="text.secondary">{t('selectFranchise')}</Typography>
        ),
      });
    }

    return items;
  }, [
    t,
    name,
    nameError,
    description,
    canWrite,
    canViewMenus,
    accessibleFranchises,
    handleFranchiseChange,
    isEditMode,
    franchiseId,
    menus,
    isMenusLoading,
    assignedMenuIds,
    originalAssignedMenuIds,
    navigate,
  ]);

  useEffect(() => {
    if (tabs.some((tab) => tab.id === activeTabId)) return;
    setActiveTabId(tabs[0]?.id ?? 'overview');
  }, [tabs, activeTabId]);

  return (
    <DetailPageTemplate
      title={isEditMode ? t('editRange') : t('createRange')}
      loading={isLoading}
      backButton={(
        <BackButton
          title={t('backToRanges')}
          isLink={false}
          handleClick={() => navigate('/range/ranges')}
        />
      )}
      headlineContent={{
        layout: 'six',
        insights: [
          <InsightTile
            key="franchise"
            label={t('franchise')}
            value={franchiseForDisplay?.franchiseName ?? '-'}
            description={t('rangeFranchiseTileDescription')}
            icon={<BusinessOutlined />}
            variant="secondary"
          />,
          <InsightTile
            key="menus"
            label={t('menus')}
            value={assignedMenuIds.size}
            description={t('rangeMenusTileDescription')}
            icon={<MenuBookOutlined />}
            variant="primary"
            loading={isMenusLoading}
          />,
          <InsightTile
            key="activeMenus"
            label={t('displayStatusActive')}
            value={activeMenusCount}
            description={t('rangeActiveMenusTileDescription')}
            icon={<CheckCircleOutline />}
            variant="success"
            loading={isMenusLoading}
          />,
          <InsightTile
            key="scheduledMenus"
            label={t('displayStatusScheduled')}
            value={scheduledMenusCount}
            description={t('rangeScheduledMenusTileDescription')}
            icon={<MenuBookOutlined />}
            variant="secondary"
            loading={isMenusLoading}
          />,
          <InsightTile
            key="draftMenus"
            label={t('displayStatusDraft')}
            value={draftMenusCount}
            description={t('rangeDraftMenusTileDescription')}
            icon={<EditNoteOutlined />}
            variant="default"
            loading={isMenusLoading}
          />,
          <InsightTile
            key="modified"
            label={t('modifiedOn')}
            value={formatDate(modifiedDate)}
            description={t('rangeLastModifiedTileDescription')}
            icon={<UpdateOutlined />}
            variant="default"
          />,
        ],
      }}
      tabs={tabs}
      activeTabId={activeTabId}
      onTabChange={setActiveTabId}
      actions={(
        <>
          <Button
            variant="secondary"
            onClick={() => navigate('/range/ranges')}
            disabled={isSaving}
          >
            {t('cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isSaving || !canWrite}
          >
            {t('save')}
          </Button>
        </>
      )}
    >
      <MessagesSnackbar
        open={isSaving}
        onClose={() => undefined}
        message={t('rangeSavingMessage')}
        severity="info"
        duration={null}
      />
      <MessagesSnackbar
        open={Boolean(successMessage)}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
        severity="success"
        duration={4000}
      />
      <MessagesSnackbar
        open={Boolean(errorMessage)}
        onClose={() => setErrorMessage('')}
        message={errorMessage}
        severity="error"
        duration={5000}
      />
    </DetailPageTemplate>
  );
};

export default RangeForm;

